import React, { useState } from "react"
import { Breadcrumb, Paginator, TitleScreen } from "components/Core"
import SearchBarFilter from "components/SearchBarFilter"
import { useSeriesList } from "./hooks/useSeriesList"
import { HiViewGrid, HiViewList, HiOutlineSearch } from "react-icons/hi"
import cn from "classnames"

// Modular Components
import ListItemCard from "./components/ListItemCard"
import SeriesTableContent from "./components/TableContent/SeriesTableContent"
import ItemPreviewModal from "./components/Modals/ItemPreviewModal"
import ProductPreviewModal from "./components/Modals/ProductPreviewModal"
import StoresListModal from "./components/Modals/StoresListModal"
import RelatedOrdersModal from "./components/Modals/RelatedOrdersModal"
import ConfirmStateChangeModal from "./components/Modals/ConfirmStateChangeModal"
import SeriesHistoryModal from "./components/Modals/SeriesHistoryModal"
import CreateSerieModal from "./components/Modals/CreateSerieModal"
import CsvBulkUpdateModal from "./components/Modals/CsvSearchModal"
import LatestMovementsWidget from "./components/Widgets/LatestMovementsWidget"
import { HiOutlinePlus, HiOutlineDocumentText } from "react-icons/hi"

const ListScreen = () => {
  const {
    viewMode, setViewMode,
    isPreviewOpen, setIsPreviewOpen,
    selectedItem,
    isRelatedOrdersModalOpen, setIsRelatedOrdersModalOpen,
    selectedSerieForOrders, setSelectedSerieForOrders,
    isConfirmStateModalOpen, setIsConfirmStateModalOpen,
    serieToUpdate, pendingStateChange,
    onRequestStateChange, onConfirmStateChangeSuccess,
    isHistoryModalOpen, setIsHistoryModalOpen,
    serieForHistory, onHistoryPreview,
    skuSearch, setSkuSearch,
    serieSapSearch, setSerieSapSearch,
    estadoOmnixSearch, setEstadoOmnixSearch,
    isAvailableSearch, setIsAvailableSearch,
    openFilterColumn, setOpenFilterColumn,
    isProductModalOpen, setIsProductModalOpen,
    selectedProduct,
    isStoresModalOpen, setIsStoresModalOpen,
    storesForModal, setStoresForModal,
    loading_app, loading_product,
    data_product,
    current_data,
    current_page, handle_page_change,
    per_page, handle_per_page,
    total_records, total_pages,
    apply_filter, remove_filter,
    handlePreview,
    handleProductPreview,
    columns,
    data_table_items,
    search_error,
    refreshSearch,
    sortField,
    sortOrder,
    handleSort,
  } = useSeriesList()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [serieToClone, setSerieToClone] = useState<any>(null)
  const [isCsvSearchModalOpen, setIsCsvSearchModalOpen] = useState(false)

  const handleClone = (serie: any) => {
    setSerieToClone(serie)
    setIsCreateModalOpen(true)
  }

  const ViewToggle = () => (
    <div className="flex bg-gray-200/50 p-1 rounded-xl border border-gray-200 shadow-inner overflow-hidden">
      <button
        onClick={() => setViewMode("BLOCK")}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-bold transition-all duration-300",
          viewMode === "BLOCK" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700",
        )}
      >
        <HiViewGrid className="text-lg" />
        Bloques
      </button>
      <button
        onClick={() => setViewMode("TABLE")}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-bold transition-all duration-300",
          viewMode === "TABLE" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700",
        )}
      >
        <HiViewList className="text-lg" />
        Tabla
      </button>
    </div>
  )

  return (
    <div className="w-full h-full overflow-auto flex flex-col items-center justify-start bg-[#F2F2F7]">
      <style>{`
        @keyframes slideDownFader {
          0% { opacity: 0; transform: translateY(-6px) scaleY(0.95); }
          100% { opacity: 1; transform: translateY(0) scaleY(1); }
        }
        .animate-slide-down-fader {
          animation: slideDownFader 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          transform-origin: top;
        }
      `}</style>
      
      <Breadcrumb data={[{ name: "Listado de series", href: "/series" }]} />
      
      <div className="w-full flex flex-col justify-center items-center py-4 px-6 mb-2">
        <TitleScreen title="Listado de series" />
      </div>

      <div className="w-full h-full flex flex-col xl:flex-row p-6 gap-6 items-start xl:items-stretch">
        <div className="flex-1 w-full min-w-0 flex flex-col gap-6">
          <div className="w-full flex flex-col bg-white p-4 md:p-5 rounded-none shadow-sm border border-gray-100 gap-4 md:gap-5">
          <div className="w-full flex flex-col lg:flex-row items-stretch lg:items-start justify-between gap-4">
            <div className="flex-1 w-full">
              <SearchBarFilter handle_page_change={handle_page_change} />
            </div>
            <div className="hidden lg:flex items-start justify-end pt-1 gap-3">
              <button
                onClick={() => setIsCsvSearchModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-[13px] font-bold transition-all shadow-sm hover:shadow-teal-200 active:scale-[0.97]"
              >
                <HiOutlineDocumentText className="text-lg" />
                Carga por CSV
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[13px] font-bold transition-all shadow-sm hover:shadow-indigo-200 active:scale-[0.97]"
              >
                <HiOutlinePlus className="text-lg" />
                Nueva Serie
              </button>
              <ViewToggle />
            </div>
          </div>
          
          <div className="w-full flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 px-1">
            <div className="w-full md:w-auto flex flex-row items-center justify-between">
              <h4 className="text-[12px] md:text-[13px] font-black text-gray-400 uppercase tracking-widest leading-tight">
                {total_records} <span className="hidden md:inline">series</span>
              </h4>
              <div className="block lg:hidden">
                <ViewToggle />
              </div>
            </div>
            <div className="w-full md:w-auto flex justify-center md:justify-end scale-[0.90] origin-center md:origin-right">
              <Paginator
                current_page={current_page}
                handle_page_change={handle_page_change}
                per_page={per_page}
                total_records={total_records}
                total_pages={total_pages}
                handle_per_page={handle_per_page}
              />
            </div>
          </div>
        </div>

        <div className="w-full flex-1 min-h-0 flex flex-col gap-4">
          <div className="w-full overflow-x-auto rounded-none border border-gray-200 shadow-lg bg-white/80 backdrop-blur-xl animate-slide-down-fader flex-1 overflow-y-auto">
            {viewMode === "BLOCK" ? (
              data_table_items?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
                  {current_data.map((item: any, i: number) => (
                    <ListItemCard key={i} item={item} onPreview={handlePreview} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  {loading_app ? (
                    <div className="flex items-center gap-3">
                      <span className="h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                      <span className="text-gray-500 font-medium">Cargando series...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <HiOutlineSearch className="text-4xl text-gray-300" />
                      <p className="text-gray-400 font-medium italic">
                        {search_error?._detail || "No se encontraron series que coincidan con la búsqueda"}
                      </p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <SeriesTableContent
                columns={columns}
                current_data={current_data}
                data_table_items={data_table_items}
                loading_app={loading_app}
                skuSearch={skuSearch}
                setSkuSearch={setSkuSearch}
                serieSapSearch={serieSapSearch}
                setSerieSapSearch={setSerieSapSearch}
                estadoOmnixSearch={estadoOmnixSearch}
                setEstadoOmnixSearch={setEstadoOmnixSearch}
                isAvailableSearch={isAvailableSearch}
                setIsAvailableSearch={setIsAvailableSearch}
                openFilterColumn={openFilterColumn}
                setOpenFilterColumn={setOpenFilterColumn}
                apply_filter={apply_filter}
                remove_filter={remove_filter}
                onPreview={handlePreview}
                onProductPreview={handleProductPreview}
                onStoresModalOpen={(stores) => {
                  setStoresForModal(stores)
                  setIsStoresModalOpen(true)
                }}
                onRelatedOrdersPreview={(item: any) => {
                  setSelectedSerieForOrders(item)
                  setIsRelatedOrdersModalOpen(true)
                }}
                onHistoryPreview={onHistoryPreview}
                onRequestStateChange={onRequestStateChange}
                search_error={search_error}
                refreshSearch={refreshSearch}
                sortField={sortField}
                sortOrder={sortOrder}
                handleSort={handleSort}
                onClone={handleClone}
              />
            )}
          </div>

          <div className="w-full flex justify-end mt-2 mb-2 pr-2 scale-90 origin-right">
            <Paginator
              current_page={current_page}
              handle_page_change={handle_page_change}
              per_page={per_page}
              total_records={total_records}
              total_pages={total_pages}
              handle_per_page={handle_per_page}
            />
          </div>
        </div>
        </div>

        {/* Right Sidebar Panels */}
        <div className="w-full xl:w-[340px] shrink-0 flex flex-col gap-4 sticky top-6" style={{ maxHeight: "calc(100vh - 3rem)" }}>
          <div className="border border-gray-100 shadow-sm bg-white overflow-hidden rounded-xl flex-1 flex flex-col" style={{ minHeight: "300px" }}>
            <LatestMovementsWidget 
              title="Últimas actualizaciones" 
              actions={["UPDATE", "BULK_UPDATE"]} 
              sortField="_omnixStateMachine.currentState.date" 
            />
          </div>
          <div className="border border-gray-100 shadow-sm bg-white overflow-hidden rounded-xl flex-1 flex flex-col" style={{ minHeight: "300px" }}>
            <LatestMovementsWidget 
              title="Últimas series agregadas" 
              actions={["CREATE"]} 
              sortField="_omnixStateMachine.currentState.date" 
            />
          </div>
        </div>
      </div>

      <ItemPreviewModal 
        isOpen={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)} 
        selectedItem={selectedItem} 
      />
      
      <ProductPreviewModal 
        isOpen={isProductModalOpen} 
        onClose={() => setIsProductModalOpen(false)} 
        selectedProduct={selectedProduct} 
        loadingProduct={loading_product} 
        skuTitle={data_product?.records?.[0]?.sku} 
      />
      
      <StoresListModal 
        isOpen={isStoresModalOpen} 
        onClose={() => setIsStoresModalOpen(false)} 
        stores={storesForModal} 
      />

      {isRelatedOrdersModalOpen && (
        <RelatedOrdersModal
          isOpen={isRelatedOrdersModalOpen}
          onClose={() => setIsRelatedOrdersModalOpen(false)}
          serie={selectedSerieForOrders}
        />
      )}

      {isConfirmStateModalOpen && (
        <ConfirmStateChangeModal
          isOpen={isConfirmStateModalOpen}
          onClose={() => setIsConfirmStateModalOpen(false)}
          serieToUpdate={serieToUpdate}
          newStateId={pendingStateChange?.id || 0}
          newStateLabel={pendingStateChange?.label || ""}
          onSuccess={onConfirmStateChangeSuccess}
        />
      )}

      {isHistoryModalOpen && (
        <SeriesHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          serie={serieForHistory}
        />
      )}

      <CreateSerieModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          setSerieToClone(null)
        }}
        onSuccess={() => refreshSearch()}
        initialData={serieToClone}
      />

      <CsvBulkUpdateModal
        isOpen={isCsvSearchModalOpen}
        onClose={() => setIsCsvSearchModalOpen(false)}
        onSuccess={() => refreshSearch()}
      />
    </div>
  )
}

export default ListScreen
